import React, { FC, useState, useEffect, useRef, ChangeEvent } from 'react';
import useDidUpdate from '../../../../hooks/useDidUpdate.effect';
import { documentService } from '../../../../utils/window.utils';
import Icon from '../../../@shared/icon';
import HiglightedOption from '../../../@shared/highlightedOption';
import { TDropdownListField } from '../../types/sitecoreJssForm.types';
import useFirstUserAction from '../../hooks/useFirstUserActionState';
import CustomTextLabel from '../@shared/customTextLabel';

const DropdownList: FC<TDropdownListField> = (props) => {
  const { field, tracker, value } = props;
  const hasAnyError = !!props.errors?.length;
  const label = field.model.title;
  const id = field.valueField.id;
  const name = field.valueField.name;
  const emptyAtTopList = field?.model?.showEmptyItem;
  const FieldValidationErrors = props.fieldValidationErrorsComponent;
  const [isVisible, setIsVisible] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasTextInputFocus, setHasTextInputFocus] = useState(false);
  const useFirstItemAsEmptyItem = field?.model?.useFirstItemAsEmptyItem;

  let dropDownListItems;

  if (useFirstItemAsEmptyItem) {
    const firstItem = field?.model?.items?.[0];
    dropDownListItems = firstItem ? [{ ...firstItem, text: '' }, ...field.model.items.slice(1)] : [];
  } else {
    dropDownListItems = field?.model?.items ?? [];
  }

  const defaultListItem = dropDownListItems?.find((item) => item?.selected);
  const [initialTextValue, setInitialTextValue] = useState(defaultListItem?.text || '');
  const [textValue, setTextValue] = useState(defaultListItem?.text || '');
  const [dropdownValue, setDropdownValue] = useState(defaultListItem?.value || '');
  const [initialDropdownValue, setInitialDropdownValue] = useState(defaultListItem?.value || '');
  const dropdownRef: React.MutableRefObject<HTMLUListElement | null> = useRef(null);
  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
  const triggerFirstInteractionGTMEvent = useFirstUserAction();

  useEffect(() => {
    if (!value?.length || (value?.length && value[0] !== dropdownValue)) {
      const defaultListItem = dropDownListItems?.find((item) => item?.selected);
      setInitialTextValue(defaultListItem?.text || '');
      setTextValue(defaultListItem?.text || '');
      setDropdownValue(defaultListItem?.value || '');
      setInitialDropdownValue(defaultListItem?.value || '');
    }
  }, [value]);

  useEffect(() => {
    if (useFirstItemAsEmptyItem) {
      const sendValueForNoSelection = field?.model?.items?.[0]?.value ?? 0;
      props.onChange(field.valueField.name, [sendValueForNoSelection], props.isValid, []);
    }
  }, []);

  const onDropdownFocus = (): void => {
    setHasFocus(true);
    setHasTextInputFocus(true);
  };

  const onTextInputBlur = (): void => {
    setHasTextInputFocus(false);
  };

  const onDropdownClik = (): void => {
    setIsVisible(!isVisible);

    if (!isVisible) {
      inputRef?.current?.focus();
    } else {
      inputRef?.current?.blur();
    }
  };

  const onSelectOption = (value: string, text: string): void => {
    setDropdownValue(value);
    setTextValue(text);
    setInitialDropdownValue(value);
    setInitialTextValue(text);
    setIsVisible(false);
  };

  const onInputUpdate = (e: ChangeEvent<HTMLInputElement>): void => {
    setTextValue(e?.target?.value);
  };

  const filteredOptions =
    textValue === ''
      ? dropDownListItems
      : dropDownListItems?.filter((option) => option.text && option.text.toLowerCase().indexOf(textValue.toLocaleLowerCase()) > -1);

  const handleClickOutside = (event): void => {
    if (dropdownRef?.current?.contains(event?.target)) {
      return;
    }

    setIsVisible(false);

    if (textValue === '') {
      setTextValue('');
    } else {
      const hasMatch = dropDownListItems?.some((opt) => opt.text === textValue);

      if (!hasMatch) {
        setDropdownValue(initialDropdownValue);
        setTextValue(initialTextValue);
      } else if (textValue !== initialTextValue) {
        const currentItem = dropDownListItems?.filter((item) => item.text === textValue);
        setDropdownValue(currentItem?.length ? currentItem[0].value : '');
      }
    }

    setHasFocus(false);
  };

  useEffect(() => {
    documentService().addEventListener('mouseup', handleClickOutside);
    return (): void => {
      documentService().removeEventListener('mouseup', handleClickOutside);
    };
  }, [initialTextValue, textValue]); //eslint-disable-line

  useDidUpdate(() => {
    if (hasFocus) {
      tracker.onFocusField(field, textValue);
    } else {
      tracker.onBlurField(field, textValue);
    }
  }, [hasFocus]);

  useDidUpdate(() => {
    const newValue = dropdownValue ? [dropdownValue] : [];
    props.onChange(field.valueField.name, newValue, props.isValid, []);
    triggerFirstInteractionGTMEvent();
  }, [dropdownValue]);

  return (
    <div className="sitecore-jss-form__control sitecore-jss-form__control--dropdown-list">
      <ul className={`form-custom-dropdown ${hasAnyError ? 'form-custom-dropdown--has-error' : ''}`} ref={dropdownRef}>
        <li className={`form-custom-dropdown__input-container ${hasFocus ? 'form-custom-dropdown__input-container--has-focus' : ''}`}>
          <label
            htmlFor={id}
            className={`form-custom-dropdown__label ${
              !hasTextInputFocus && textValue === '' ? 'form-custom-dropdown__label--empty-field' : ''
            }`}
          >
            <CustomTextLabel label={label} />
          </label>
          <input
            className="form-custom-dropdown__text-input"
            type="text"
            value={textValue}
            name={name}
            onChange={onInputUpdate}
            ref={inputRef}
            id={id}
            onClick={onDropdownClik}
            onFocus={onDropdownFocus}
            onBlur={onTextInputBlur}
            autoComplete="off"
            form="novalidatedform"
          />
          <Icon
            iconName={isVisible ? 'arrow-chevron-up' : 'arrow-chevron-down'}
            size={24}
            extraClassName="form-custom-dropdown__icon"
            onClick={onDropdownClik}
          />
        </li>
        <li className={`form-custom-dropdown__list-container ${isVisible ? 'form-custom-dropdown__list-container--visible' : ''}`}>
          <ul className="form-custom-dropdown__list" role="listbox">
            {emptyAtTopList && !useFirstItemAsEmptyItem && (
              <li
                className="form-custom-dropdown__list-item form-custom-dropdown__list-item-empty"
                role="option" // eslint-disable-line
                onClick={(): void => onSelectOption('', '')}
              ></li>
            )}
            {initialTextValue !== textValue ? (
              <>
                {filteredOptions.map((item) => (
                  <li
                    className="form-custom-dropdown__list-item"
                    role="option" // eslint-disable-line
                    onClick={(): void => onSelectOption(item.value, item.text || '')}
                    key={item?.itemId}
                  >
                    <HiglightedOption keyword={textValue} optionText={item.text} />
                  </li>
                ))}
              </>
            ) : (
              <>
                {dropDownListItems?.map((item) => (
                  <li
                    className="form-custom-dropdown__list-item"
                    role="option" // eslint-disable-line
                    onClick={(): void => onSelectOption(item.value, item.text || '')}
                    key={`${item.value}-${item.text}`}
                  >
                    <HiglightedOption keyword={textValue} optionText={item.text} />
                  </li>
                ))}
              </>
            )}
          </ul>
        </li>
      </ul>
      {FieldValidationErrors && <FieldValidationErrors {...props} />}
    </div>
  );
};

export default DropdownList;
